import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { FileDown, AlertTriangle, History, Settings2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const LEDGER_FIELDS = [
  { key: 'salesLedger', label: 'Sales' },
  { key: 'cgstLedger', label: 'CGST' },
  { key: 'sgstLedger', label: 'SGST' },
  { key: 'igstLedger', label: 'IGST' },
  { key: 'tcsLedger', label: 'TCS' },
  { key: 'receiptsLedger', label: 'Receipts (Bank/Cash)' },
  { key: 'vendorPaymentsLedger', label: 'Vendor Payments (Bank/Cash)' },
];

function LedgerNameMapping({ isAdmin }) {
  const [names, setNames] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/settings/tally-ledger-names`);
      setNames(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load ledger names');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/settings/tally-ledger-names`, names);
      setNames(res.data);
      toast.success('Ledger names saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save ledger names');
    } finally {
      setSaving(false);
    }
  };

  if (!names) return null;

  return (
    <Card className="p-4" data-testid="ledger-name-mapping">
      <div className="flex items-center gap-2 mb-3">
        <Settings2 className="h-4 w-4 text-slate-500" />
        <h2 className="text-sm font-semibold text-slate-800">Ledger Name Mapping</h2>
        <span className="text-xs text-slate-400">— match these to your real Tally chart of accounts before importing</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {LEDGER_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-xs">{f.label}</Label>
            <Input
              value={names[f.key] || ''}
              disabled={!isAdmin}
              onChange={(e) => setNames({ ...names, [f.key]: e.target.value })}
              className="mt-1"
              data-testid={`ledger-name-${f.key}`}
            />
          </div>
        ))}
      </div>
      {isAdmin ? (
        <Button onClick={save} disabled={saving} className="mt-3" size="sm" data-testid="save-ledger-names-btn">
          {saving ? 'Saving…' : 'Save Ledger Names'}
        </Button>
      ) : (
        <p className="text-xs text-slate-400 mt-3">Only Admin can edit ledger names.</p>
      )}
    </Card>
  );
}

function VoucherExportPanel({ voucherType, endpointSlug }) {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(today);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [logs, setLogs] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tally/export/${endpointSlug}/preview`, {
        params: { start, end },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const downloadXml = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tally/export/${endpointSlug}/download`, {
        params: { start, end },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tally-${endpointSlug}-${start}_to_${end}.xml`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${voucherType} voucher XML downloaded`);
      fetchPreview();
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tally/export/logs`, {
        params: { voucher_type: voucherType },
      });
      setLogs(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load export log');
    }
  };

  const vouchers = (preview && preview.vouchers) || [];
  const overlap = (preview && preview.overlapWarning) || [];
  const needsAccountantInput = (preview && preview.needsAccountantInput) || [];

  return (
    <div className="space-y-6" data-testid={`${endpointSlug}-panel`}>
      <Card className="p-4 flex flex-wrap gap-3 items-end" data-testid={`${endpointSlug}-filters`}>
        <div>
          <Label className="text-xs">Start</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40 mt-1" data-testid={`${endpointSlug}-start-date`} />
        </div>
        <div>
          <Label className="text-xs">End</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40 mt-1" data-testid={`${endpointSlug}-end-date`} />
        </div>
        <Button variant="outline" onClick={fetchPreview} disabled={loading} data-testid={`${endpointSlug}-preview-btn`}>
          {loading ? 'Loading…' : 'Preview'}
        </Button>
        <Button
          onClick={downloadXml}
          disabled={downloading || !preview || vouchers.length === 0}
          className="gap-2 bg-teal-600 hover:bg-teal-700"
          data-testid={`${endpointSlug}-download-btn`}
        >
          <FileDown className="h-4 w-4" /> {downloading ? 'Exporting…' : `Download ${voucherType} XML`}
        </Button>
        <Button variant="ghost" onClick={fetchLogs} className="gap-2 ml-auto" data-testid={`${endpointSlug}-view-log-btn`}>
          <History className="h-4 w-4" /> Export Log
        </Button>
      </Card>

      {overlap.length > 0 && (
        <div
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg flex items-start gap-2"
          data-testid={`${endpointSlug}-overlap-warning`}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            This date range overlaps {overlap.length} earlier export{overlap.length === 1 ? '' : 's'}
            (most recent: {overlap[0].dateRangeStart} to {overlap[0].dateRangeEnd}, {overlap[0].voucherCount} vouchers,
            by {overlap[0].exportedBy} on {new Date(overlap[0].exportedAt).toLocaleDateString('en-IN')}). Re-exporting
            will not modify Tally, but avoid importing the same period twice.
          </span>
        </div>
      )}

      {needsAccountantInput.length > 0 && (
        <div
          className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-3 py-3 rounded-lg space-y-2"
          data-testid={`${endpointSlug}-needs-accountant-input`}
        >
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {needsAccountantInput.length} wallet-credit receipt{needsAccountantInput.length === 1 ? '' : 's'} excluded — needs accountant input
          </div>
          <p>These are wallet credit applications, not new cash — they are never exported to Tally.</p>
          <ul className="space-y-1">
            {needsAccountantInput.map((row, i) => (
              <li key={i} className="font-mono" data-testid={`${endpointSlug}-needs-accountant-input-row-${i}`}>
                {row.date} · {row.clientName} · {row.tripRef} · {inr(row.amount)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview && (
        <Card className="grid grid-cols-2 md:grid-cols-2 divide-x divide-slate-100" data-testid={`${endpointSlug}-preview-totals`}>
          <div className="p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Vouchers</div>
            <div className="text-lg font-mono mt-1 text-slate-900">{preview.count}</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Amount</div>
            <div className="text-lg font-mono mt-1 font-bold text-slate-900">{inr(preview.totalAmount)}</div>
          </div>
        </Card>
      )}

      {preview && (
        vouchers.length === 0 ? (
          <Card className="text-center py-12 text-slate-400 text-sm" data-testid={`${endpointSlug}-empty-preview`}>
            No {voucherType.toLowerCase()} vouchers in this date range.
          </Card>
        ) : (
          <Card className="overflow-x-auto" data-testid={`${endpointSlug}-voucher-table`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Trip</TableHead>
                  <TableHead>Party Ledger</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vouchers.map((v, i) => (
                  <TableRow key={v.ref || i} data-testid={`${endpointSlug}-voucher-row-${i}`}>
                    <TableCell className="font-mono text-xs">{v.date}</TableCell>
                    <TableCell className="font-mono text-xs">{v.ref}</TableCell>
                    <TableCell className="font-mono text-xs">{v.tripRef || '—'}</TableCell>
                    <TableCell className="text-xs">{v.party}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold">{inr(v.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )
      )}

      {logs && (
        <Card className="overflow-x-auto" data-testid={`${endpointSlug}-log-table`}>
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b">
            Export Log — {voucherType} Vouchers
          </div>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No exports yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Exported</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Range</TableHead>
                  <TableHead className="text-right">Vouchers</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l, i) => (
                  <TableRow key={i} data-testid={`${endpointSlug}-log-row-${i}`}>
                    <TableCell className="text-xs">{new Date(l.exportedAt).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-xs">{l.exportedBy}</TableCell>
                    <TableCell className="font-mono text-xs">{l.dateRangeStart} → {l.dateRangeEnd}</TableCell>
                    <TableCell className="text-right text-xs">{l.voucherCount}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(l.totalAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}

export default function TallyExport() {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [tab, setTab] = useState('sales');

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="tally-export-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <FileDown className="h-6 w-6 text-teal-600" />
          Tally Export
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Generate Tally-compatible XML for the accountant to import manually via Tally's own Import Data
          feature. Tally stays the source of truth — this is a one-way, read-only export.
        </p>
      </header>

      <LedgerNameMapping isAdmin={isAdmin} />

      <div className="flex gap-2">
        <button
          type="button" data-testid="tally-tab-sales"
          onClick={() => setTab('sales')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'sales' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          Sales Vouchers
        </button>
        <button
          type="button" data-testid="tally-tab-receipt"
          onClick={() => setTab('receipt')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'receipt' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          Receipt Vouchers
        </button>
        <button
          type="button" data-testid="tally-tab-payment"
          onClick={() => setTab('payment')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'payment' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          Payment Vouchers
        </button>
      </div>

      {tab === 'sales' ? (
        <VoucherExportPanel voucherType="Sales" endpointSlug="sales-vouchers" />
      ) : tab === 'receipt' ? (
        <VoucherExportPanel voucherType="Receipt" endpointSlug="receipt-vouchers" />
      ) : (
        <VoucherExportPanel voucherType="Payment" endpointSlug="payment-vouchers" />
      )}
    </div>
  );
}
